// Assembling a review.
//
// Findings are grouped by evidence strength, never by severity. There is no
// severity in this product, no score and no grade: a reader is told how a
// finding was established and which audit records the same cause, and decides
// for themselves.

import type { Flag, FlagDraft } from '../types';
import { getFailureMode } from './taxonomy';
import { formatRrpCategory } from './rrp';
import { PISCATAWAY, type SeedAnalysis } from './seed';
import { enrichFlag } from '../engine/finding';

export interface ReportDocumentMeta {
  name: string;
  pageCount: number | null;
  ingestedAt: string;
  facility?: string;
  deliveryOrder?: string;
  preparedBy?: string;
  documentNote?: string;
  documentUrl?: string;
}

export interface ReportData {
  id: string;
  document: ReportDocumentMeta;
  flags: Flag[];
  ranChecks: string[];
  /** How these flags were produced. Stated on the sheet, never implied. */
  provenance: string;
}

/** The check ids the arithmetic pass can run, for the empty state's "what ran" list. */
export const CHECK_LABELS: Record<string, string> = {
  'citation-check': 'Citation resolution — is every external reference pinned to a version and date',
  'source-attribution-check':
    'Source attribution — does every estimated parameter cite a permitted source',
  'generation-check':
    'IDIQ generation lookup — did the governing contract require baseline witnessing',
  'completeness-check':
    'Required contents — is anything on the FEMP §2.2.4 list missing',
  'table-reconciliation-check':
    'Table reconciliation — do the savings tables agree with each other',
  'model-classification':
    'Pattern match — clause matching against the published detection signals',
};

/* ==========================================================================
   Evidence badges
   ==========================================================================

   One badge per finding, naming how it was established. Never a severity, a
   score or a confidence. Each carries the one-line definition shown on hover
   and focus and repeated in the glossary, so a badge and the sentence that
   explains it cannot drift apart. */

export type EvidenceBadgeId = 'arithmetic' | 'audit-precedent' | 'guidance-warning';

export interface EvidenceBadge {
  id: EvidenceBadgeId;
  label: string;
  /** Shown on hover and focus, and in the glossary. One sentence. */
  definition: string;
  tone: 'blue' | 'amber';
}

export const EVIDENCE_BADGES: Record<EvidenceBadgeId, EvidenceBadge> = {
  arithmetic: {
    id: 'arithmetic',
    label: 'Arithmetic',
    definition: 'Ceiling re-did the plan’s own maths. You can check it on paper.',
    tone: 'blue',
  },
  'audit-precedent': {
    id: 'audit-precedent',
    label: 'Audit precedent',
    definition:
      'A named federal audit records this exact cause producing a real dispute or dollar loss.',
    tone: 'amber',
  },
  'guidance-warning': {
    id: 'guidance-warning',
    label: 'Guidance warning',
    definition:
      'FEMP’s own guidance warns about this. No audit case is attached to it yet.',
    tone: 'amber',
  },
};

/** The single badge a finding carries. Tier is read here and nowhere visible. */
export function evidenceBadgeFor(flag: Pick<Flag, 'layer' | 'tier'>): EvidenceBadge {
  if (flag.layer === 'deterministic') return EVIDENCE_BADGES.arithmetic;
  return flag.tier === 1
    ? EVIDENCE_BADGES['audit-precedent']
    : EVIDENCE_BADGES['guidance-warning'];
}

/**
 * "10 findings · 3 you can check on paper · 7 matched to a clause in the plan".
 * Counts, never a score.
 */
export function summaryLine(flags: Flag[]): string {
  const total = flags.length;
  const deterministic = flags.filter((f) => f.layer === 'deterministic').length;
  const model = total - deterministic;
  const noun = total === 1 ? 'finding' : 'findings';
  return `${total} ${noun} · ${deterministic} you can check on paper · ${model} matched to a clause in the plan`;
}

/** Taxonomy detail a card needs, resolved once. */
export interface FlagContext {
  failureModeName: string;
  whyItProducesDispute: string;
  rrpLabel: string;
  whereItAppears: string;
}

export function contextFor(flag: Flag): FlagContext {
  const fm = getFailureMode(flag.failureModeId);
  return {
    failureModeName: fm.name,
    whyItProducesDispute: fm.why_it_produces_dispute,
    rrpLabel: formatRrpCategory(fm.rrp_category),
    whereItAppears: fm.where_it_appears,
  };
}

/* ==========================================================================
   Sources
   ========================================================================== */

function seedToReport(seed: SeedAnalysis): ReportData {
  return {
    id: seed.documentId,
    document: {
      name: seed.documentTitle,
      pageCount: 35,
      ingestedAt: seed.documentDate,
      facility: seed.facility,
      deliveryOrder: seed.deliveryOrder,
      preparedBy: seed.preparedBy,
      documentNote: seed.documentNote,
      documentUrl: seed.documentUrl,
    },
    // The seed file is already Flag-shaped; it carries a check on the three
    // arithmetic findings and none on the rest. It is hand-transcribed rather
    // than engine output, so it has no plan area, claim, context line or
    // comment draft of its own — they are derived here through the same
    // function the checks use, so the example and a real review cannot end up
    // describing the same failure mode differently.
    flags: (seed.flags as unknown as FlagDraft[]).map(enrichFlag),
    ranChecks: [
      'table-reconciliation-check',
      'source-attribution-check',
      'citation-check',
      'model-classification',
    ],
    provenance:
      'This example was worked by hand against the plan text. Ceiling’s arithmetic checks reproduce the first three findings; the rest are transcribed from that manual review.',
  };
}
const SEEDED_REPORTS: Record<string, () => ReportData> = {
  piscataway: () => seedToReport(PISCATAWAY),
};

export function getSeededReport(id: string | undefined): ReportData | null {
  if (!id) return null;
  const build = SEEDED_REPORTS[id];
  return build ? build() : null;
}
